import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { Popover, Button } from 'antd'
import { ControlOutlined } from '@ant-design/icons'

// Utils
import { removeAccents } from 'utils/filters'

// Components
import Accordion from './subcomponents/Accordion'
import TriggerHeader from './subcomponents/TriggerHeader'
import Content from './subcomponents/Content'

// Styles
import './index.scss'

const searchFilterOptions = async () => []

const debouncedOnChange = _.debounce((value, onChange) => {
	onChange(value)
}, 400)

function isOptionSelected(selected, optionValue) {
	if (selected === undefined || selected === null) return false

	const stringOptionValue = String(optionValue)

	if (Array.isArray(selected)) {
		return selected.map(String).includes(stringOptionValue)
	} else {
		return String(selected) === stringOptionValue
	}
}

function checkOptionsActive({ selected, option }) {
	return isOptionSelected(selected, option.value)
}

function FilterButton({
	title = '',
	selected,
	options = [],
	filterKey,
	filterIndex,
	currentSection,
	hasAsyncSearch,
	requestProps,
	customFieldsId,
	type = 'checkbox',
	findElements = false,
	startsOpen = false,
	changeType = 'auto',
	onCustomClear,
	onChange = () => {},
	isPopover = true,
	asyncFetch,
}) {
	const [renderList, setRenderList] = useState([])
	const [search, setSearch] = useState('')
	const [debouceSearch, setDebouceSearch] = useState('')
	const [expanded, setExpanded] = useState(false)
	const [loadingOptions, setLoadingOptions] = useState(false)

	const [popoverVisible, setPopoverVisible] = useState(false)
	const dispatch = useDispatch()

	const accessToken = useSelector((state) => {
		const appUser = state.app?.user
		const sessionUser = state.session
		return appUser?.accessToken || sessionUser?.accessToken || 'guest-token'
	})

	useEffect(() => {
		if (isPopover && popoverVisible) {
			setExpanded(true)
		}
	}, [isPopover, popoverVisible])

	const shouldFetch = type !== 'date' && !!accessToken && expanded

	const { data: filterOptions, status } = useQuery({
		queryKey: ['filter-options', filterKey, debouceSearch],
		queryFn: ({ signal }) => fetchOptions(signal),
		staleTime: 1000 * 60 * 10,
		enabled: shouldFetch,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		retry: false,
	})

	async function fetchOptions(signal) {
		setLoadingOptions(true)
		if (expanded) {
			if (!filterKey) {
				setLoadingOptions(false)
				return options
			}

			const parts = filterKey.split('.')
			const key = parts[0]
			const isCustomField = parts[1]

			try {
				if (asyncFetch) {
					const data = await asyncFetch(debouceSearch)
					setLoadingOptions(false)
					return data
				}
				if (customFieldsId || (isCustomField && debouceSearch)) {
					const customFieldFilterOptions = await searchFilterOptions(
						customFieldsId ?? key,
						debouceSearch,
						signal
					)
					setLoadingOptions(false)
					return customFieldFilterOptions
				}
			} catch (error) {
				console.error(error)
			}
		}

		setLoadingOptions(false)
		return options.map((option) => ({
			...option,
			active: checkOptionsActive({ selected, option }),
			visible: true,
		}))
	}

	const changeRenderList = useCallback(
		(action = 'change') => {
			if (type === 'date') return

			const currentOptions =
				filterOptions && filterOptions.length > 0 ? filterOptions : options

			if (action === 'clear') {
				setRenderList(
					currentOptions.map((option) => ({
						...option,
						active: false,
						visible: true,
					}))
				)
			} else {
				setRenderList(
					currentOptions.map((option) => ({
						...option,
						active: checkOptionsActive({ selected, option }),
						visible: true,
					}))
				)
			}
		},
		[options, selected, filterOptions, type]
	)

	useEffect(() => {
		if (startsOpen) setExpanded(true)
	}, [startsOpen])

	useEffect(() => {
		if (options.length > 0) changeRenderList()
	}, [options, selected, changeRenderList])

	useEffect(() => {
		if (status === 'success' && filterOptions && type !== 'date') {
			const formattedList = filterOptions.map((option) => ({
				...option,
				visible: true,
				active: isOptionSelected(selected, option.value),
			}))

			setRenderList(formattedList)
			setLoadingOptions(false)
		}
	}, [
		status,
		filterOptions,
		currentSection,
		filterIndex,
		selected,
		dispatch,
		type,
	])

	function handleChangeSearch(e) {
		const value = e.target.value
		setSearch(value)
		setRenderList(
			renderList.map((e) =>
				removeAccents(e.label.toLowerCase()).includes(
					removeAccents(value.toLowerCase())
				)
					? { ...e, visible: true }
					: { ...e, visible: false }
			)
		)
	}

	function handleAsyncSearch(e) {
		const value = e.target.value
		setSearch(value)
		debouncedOnChange(value, setDebouceSearch)
	}

	function handleClickCheckbox(item) {
		const newList = renderList.map((e) =>
			e.value === item.value ? { ...e, active: !e.active } : e
		)
		setRenderList(newList)
		changeType === 'auto' &&
			onChange(newList.filter((e) => e.active).map((e) => e.value))
	}

	function handleClickRadio(item) {
		const newList = renderList.map((e) =>
			e.value === item.value ? { ...e, active: true } : { ...e, active: false }
		)
		setRenderList(newList)
		changeType === 'auto' && onChange(item.value)
	}

	function handleClickClear() {
		if (type === 'date') {
			onChange([])
			if (onCustomClear) onCustomClear()
			return
		}

		changeRenderList('clear')
		setSearch('')
		setDebouceSearch('')
		onChange(type === 'checkbox' ? [] : '')
		if (onCustomClear) onCustomClear()
	}

	function handleClickFilter() {
		const selectedValues = renderList
			.filter((e) => e.active)
			.map((e) => e.value)
		onChange(type === 'checkbox' ? selectedValues : selectedValues[0])
	}

	const filterContent = (
		<section
			className={`filter-accordion ${isPopover ? 'is-popover-content' : ''}`}>
			<Accordion
				{...(startsOpen ? { defaultValue: 'item-1' } : {})}
				activeKey={expanded ? 'item-1' : null}
				triggerHeader={<TriggerHeader title={title} />}
				content={
					<Content
						findElements={findElements && type !== 'date'}
						renderList={renderList}
						type={type}
						selected={selected}
						loadingOptions={loadingOptions}
						hasAsyncSearch={hasAsyncSearch}
						debouceSearch={debouceSearch}
						onChange={onChange}
						search={search}
						changeType={changeType}
						handleAsyncSearch={handleAsyncSearch}
						handleChangeSearch={handleChangeSearch}
						handleClickClear={handleClickClear}
						handleClickCheckbox={handleClickCheckbox}
						handleClickRadio={handleClickRadio}
						handleClickFilter={handleClickFilter}
					/>
				}
				hasTriggerBody={false}
				hasChevronText={false}
				onExpand={() => setExpanded(true)}
				ariaLabelReference="open-more-filters"
				onChange={(key) => {
					if (!isPopover) setExpanded(!!key)
				}}
			/>
		</section>
	)

	if (isPopover) {
		return (
			<Popover
				content={<div style={{ width: 300 }}>{filterContent}</div>}
				trigger="click"
				placement="bottomRight"
				open={popoverVisible}
				onOpenChange={setPopoverVisible}
				overlayClassName="filter-accordion-popover">
				<Button
					type="text"
					className="filter-trigger-btn"
					icon={<ControlOutlined />}
					size="large">
					Mais filtros
				</Button>
			</Popover>
		)
	}

	return filterContent
}

export default FilterButton
